import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { DetailShell, Section, Grid } from './detail/DetailShell';
import RouteMap from './routePlan/RouteMap';
import { formatDateTime, formatMinutes } from './FieldVisitsPage';

const API_BASE = '/api/visitsessions';

const TABS = [
  { key: 'summary', label: 'Özet', icon: 'grid' },
  { key: 'visits', label: 'Ziyaretler', icon: 'clock' },
  { key: 'insights', label: 'İçgörüler', icon: 'sparkle' },
];

const KIND_META = {
  VISIT_START: { label: 'Ziyaret başladı', color: 'var(--accent)', dot: 'var(--accent)' },
  VISIT_END: { label: 'Ziyaret bitti', color: '#16a34a', dot: '#16a34a' },
  ORDER: { label: 'Sipariş', color: '#7c3aed', dot: '#7c3aed' },
  COLLECTION: { label: 'Tahsilat', color: 'var(--warn)', dot: 'var(--warn)' },
};

function pick(obj, ...keys) {
  for (const k of keys) if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

function money(v) {
  if (v == null) return '-';
  return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function timeOnly(v) {
  if (!v) return '--:--';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '--:--' : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function visitStatusBadge(status) {
  if (status === 'Completed') return <StatusBadge tone="ok">Tamamlandı</StatusBadge>;
  if (status === 'Cancelled') return <StatusBadge tone="bad">İptal</StatusBadge>;
  if (status === 'InProgress') return <StatusBadge tone="wait">Devam ediyor</StatusBadge>;
  return <StatusBadge tone="neutral">{status || '-'}</StatusBadge>;
}

/** Zaman çizelgesi — ziyaret satırı açıldığında o ziyaretin olayları burada listelenir. */
function Timeline({ events, emptyText }) {
  if (!events.length) return emptyText ? <div style={{ color: '#64748b', fontSize: 13 }}>{emptyText}</div> : null;
  return (
    <div style={{ position: 'relative', paddingLeft: 28 }}>
      <div style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 2, background: 'rgba(15,23,42,0.10)' }} />
      {events.map((e, i) => {
        const kind = pick(e, 'kind', 'Kind');
        const meta = KIND_META[kind] || { label: kind, dot: '#94a3b8', color: '#334155' };
        const at = pick(e, 'at', 'At');
        const customer = pick(e, 'customerTitle', 'CustomerTitle');
        const amount = pick(e, 'amount', 'Amount');
        const note = pick(e, 'note', 'Note');
        const status = pick(e, 'status', 'Status');
        const lat = pick(e, 'latitude', 'Latitude');
        const lng = pick(e, 'longitude', 'Longitude');
        return (
          <div key={`${kind}-${i}`} style={{ position: 'relative', paddingBottom: 14 }}>
            <span style={{
              position: 'absolute', left: -24, top: 4, width: 12, height: 12,
              borderRadius: '50%', background: meta.dot, border: '2px solid #fff',
              boxShadow: '0 0 0 2px rgba(15,23,42,0.08)',
            }} />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: '#0f172a' }}>{timeOnly(at)}</strong>
              <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{meta.label}</span>
              {customer && <span style={{ fontSize: 13, color: '#334155' }}>{customer}</span>}
              {amount != null && <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{money(amount)}</span>}
              {kind === 'COLLECTION' && status && <span style={{ fontSize: 12, color: '#64748b' }}>{status}</span>}
              {lat != null && lng != null && (
                <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>
                  Konum
                </a>
              )}
            </div>
            {note && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{note}</div>}
          </div>
        );
      })}
    </div>
  );
}

/** Tek bir metrik kutusu — DetailShell içinde olduğu için .dps kökü altında. */
function Stat({ label, value, hint, tone }) {
  const color = tone === 'good' ? '#16a34a' : tone === 'warn' ? 'var(--warn)' : tone === 'bad' ? '#dc2626' : '#0f172a';
  return (
    <div style={{
      background: '#fff', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 12,
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</span>
      {hint && <span style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</span>}
    </div>
  );
}

/**
 * İçgörü kartı. Burada gerçek bir model çalışmıyor — metinler aşağıdaki
 * buildInsights() tarafından metriklerden deterministik olarak üretiliyor.
 */
function Insight({ tone = 'info', title, body }) {
  const palette = {
    good: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' },
    warn: { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
    bad: { bg: '#fef2f2', bd: '#fecaca', fg: '#991b1b' },
    info: { bg: '#eff6ff', bd: '#bfdbfe', fg: '#1e40af' },
  }[tone];
  return (
    <div style={{ background: palette.bg, border: `1px solid ${palette.bd}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: palette.fg, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

/**
 * Metriklerden içgörü üretir. Kural tabanlı ve deterministik: aynı veri hep aynı
 * cümleleri verir. Buradaki eşikler saha ekibiyle kalibre edilmeli.
 */
function buildInsights(metrics, visits, timeline, reasonName = (c) => c) {
  if (!metrics) return [];
  const out = [];
  const { visitCount, completedCount, cancelledCount, customerCount,
    sessionMinutes, visitMinutes, idleMinutes, avgVisitMinutes,
    orderCount, orderTotal, collectionTotal, conversionRate } = metrics;

  if (visitCount === 0) {
    out.push({ tone: 'bad', title: 'Bu oturumda hiç ziyaret yok', body: 'Gün başlatılmış ama tek bir müşteri ziyareti kaydedilmemiş. Cihazda ziyaret başlatılamamış olabilir.' });
    return out;
  }

  const idleShare = sessionMinutes > 0 ? Math.round((idleMinutes / sessionMinutes) * 100) : 0;
  if (idleShare >= 60) {
    out.push({ tone: 'warn', title: `Sürenin %${idleShare}'i ziyaret dışında geçmiş`, body: `Oturum ${formatMinutes(sessionMinutes)} sürmüş, bunun ${formatMinutes(visitMinutes)}'i müşteri içinde. Kalan süre yol, mola veya beklemede geçmiş görünüyor.` });
  } else if (idleShare <= 25) {
    out.push({ tone: 'good', title: 'Süre verimli kullanılmış', body: `Oturumun yalnız %${idleShare}'i ziyaret dışında geçmiş. Rota sıkışıklığı iyi.` });
  }

  if (conversionRate >= 70) {
    out.push({ tone: 'good', title: `Satış dönüşümü yüksek (%${conversionRate})`, body: `Ziyaret edilen ${customerCount} müşterinin çoğundan sipariş çıkmış. Toplam ${money(orderTotal)} tutarında ${orderCount} sipariş.` });
  } else if (conversionRate < 30 && customerCount >= 3) {
    out.push({ tone: 'warn', title: `Satış dönüşümü düşük (%${conversionRate})`, body: `${customerCount} müşteri ziyaret edilmiş ama yalnız ${orderCount} sipariş girilmiş. Satamama nedenlerine bakmakta fayda var.` });
  }

  if (cancelledCount > 0) {
    // #300: kodlar NonSaleReason açıklamasına çözümlenir ("1" değil "Dükkan Kapalı").
    const reasons = visits.filter((v) => v.status === 'Cancelled' && v.reasonCode).map((v) => reasonName(v.reasonCode));
    const uniq = [...new Set(reasons)];
    out.push({
      tone: 'warn',
      title: `${cancelledCount} ziyaret iptal edilmiş`,
      body: uniq.length ? `En sık neden: ${uniq.slice(0, 3).join(', ')}.` : 'İptal nedeni girilmemiş — neden kodu zorunlu değilse veri kalitesi düşüyor.',
    });
  }

  if (avgVisitMinutes > 0 && avgVisitMinutes < 3 && completedCount >= 3) {
    out.push({ tone: 'warn', title: 'Ziyaretler çok kısa', body: `Ortalama ziyaret ${formatMinutes(avgVisitMinutes)}. Bu kadar kısa süre gerçek bir raf/stok kontrolü için genelde yetmez.` });
  }

  const noGps = visits.filter((v) => v.startLatitude == null || v.startLongitude == null).length;
  if (noGps > 0) {
    out.push({ tone: 'info', title: `${noGps} ziyarette konum yok`, body: 'Rut ekranından başlatılan ziyaretler GPS yazmıyor; yalnız müşteri detayından başlatılanlar konum taşıyor.' });
  }

  if (collectionTotal > 0) {
    out.push({ tone: 'info', title: 'Tahsilat yapılmış', body: `Oturum penceresinde toplam ${money(collectionTotal)} tahsilat kaydı var. Bu eşleştirme temsilci + zaman aralığına göredir, ziyarete birebir bağlı değildir.` });
  }

  const gaps = [];
  for (let i = 1; i < timeline.length; i += 1) {
    const prev = new Date(timeline[i - 1].at ?? timeline[i - 1].At).getTime();
    const cur = new Date(timeline[i].at ?? timeline[i].At).getTime();
    const diffMin = (cur - prev) / 60000;
    if (diffMin >= 60) gaps.push(diffMin);
  }
  if (gaps.length) {
    out.push({ tone: 'info', title: `${gaps.length} uzun boşluk`, body: `Olaylar arasında 1 saatten uzun ${gaps.length} aralık var; en uzunu ${formatMinutes(Math.max(...gaps))}.` });
  }

  return out;
}

export function FieldVisitDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  usePageMeta('Saha Ziyareti', 'Gün oturumu detayı');

  const [tab, setTab] = useState('summary');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  // #300 Satamama Nedenleri: reasonCode (TextId) → Description sözlüğü. Yüklenemezse ham kod kalır.
  const [reasonNames, setReasonNames] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/nonsalereasons/Select', { headers: authHeaders() });
        if (!res.ok) return;
        const payload = await res.json();
        const items = pick(payload, 'Data', 'data') ?? payload ?? [];
        if (!alive || !Array.isArray(items)) return;
        const map = {};
        for (const r of items) {
          const code = pick(r, 'TextId', 'textId');
          const name = pick(r, 'Description', 'description');
          if (code) map[code] = name || code;
        }
        setReasonNames(map);
      } catch { /* sözlük opsiyonel — ham kod gösterilir */ }
    })();
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Detay alınamadı (${res.status})`);
      const payload = await res.json();
      setData(pick(payload, 'Data', 'data') ?? payload);
    } catch (e) {
      setError(e.message || 'Detay alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const session = data?.session;
  const metrics = data?.metrics;
  const visits = useMemo(() => data?.visits ?? [], [data]);
  const orders = useMemo(() => data?.orders ?? [], [data]);
  const timeline = useMemo(() => data?.timeline ?? [], [data]);

  // Haritada yalnız konumu olan ziyaret başlangıçları; sıra numarası ziyaret sırası.
  const mapPoints = useMemo(() => visits
    .filter((v) => v.startLatitude != null && v.startLongitude != null)
    .map((v, i) => ({
      key: v.id,
      lat: Number(v.startLatitude),
      lng: Number(v.startLongitude),
      label: `${timeOnly(v.startedAt)} · ${v.customerTitle || 'Müşteri'}`,
      order: i + 1,
      color: v.status === 'Completed' ? '#16a34a' : v.status === 'Cancelled' ? '#dc2626' : '#fdab3d',
    })), [visits]);

  const insights = useMemo(
    () => buildInsights(metrics, visits, timeline, (c) => reasonNames[c] || c),
    [metrics, visits, timeline, reasonNames],
  );

  /**
   * Her olayı bir ziyaretin altına yerleştir: aynı müşteri + ziyaretin zaman penceresi.
   * Ziyaret başlangıç/bitiş olayları zaten o ziyarete aittir (kimlik yerine pencere+müşteri
   * kullanıyoruz çünkü backend timeline'ı visitId taşımıyor). Hiçbir pencereye düşmeyenler
   * `orphanEvents` olarak ayrı gösterilir — sessizce kaybolmasınlar.
   */
  const { eventsByVisit, orphanEvents } = useMemo(() => {
    const byVisit = new Map(visits.map((v) => [v.id, []]));
    const orphans = [];
    const ms = (x) => new Date(x).getTime();

    timeline.forEach((e) => {
      const at = ms(pick(e, 'at', 'At'));
      const cid = pick(e, 'customerId', 'CustomerId');
      const owner = visits.find((v) => {
        if (cid && v.customerId !== cid) return false;
        const start = ms(v.startedAt);
        const end = v.endedAt ? ms(v.endedAt) : Number.POSITIVE_INFINITY;
        return at >= start && at <= end;
      });
      if (owner) byVisit.get(owner.id).push(e);
      else orphans.push(e);
    });
    return { eventsByVisit: byVisit, orphanEvents: orphans };
  }, [visits, timeline]);

  const audit = session ? {
    createdBy: session.createdBy, createdDate: session.createdDate,
    updatedBy: session.updatedBy, updatedDate: session.updatedDate,
  } : null;

  const crumb = [
    { label: 'Raporlar' },
    { label: 'Saha Ziyaretleri', to: '/reports/field-visits' },
    { label: session ? `${session.representativeName || 'Temsilci'} · ${formatDateTime(session.startedAt)}` : 'Detay', current: true },
  ];

  const statusNode = session
    ? (session.status === 'OPEN' ? <StatusBadge tone="wait">Açık oturum</StatusBadge> : <StatusBadge tone="ok">Kapalı</StatusBadge>)
    : null;

  const actions = (
    <>
      <button type="button" className="dps-btn" onClick={() => navigate('/reports/field-visits')}>Listeye Dön</button>
      <button type="button" className="dps-btn" onClick={load} disabled={loading}>Yenile</button>
    </>
  );

  return (
    <DetailShell
      crumb={crumb}
      status={statusNode}
      tabs={TABS}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      modeless
      loading={loading}
      readOnly
      audit={audit}
    >
      {error && <Section title="Hata"><div style={{ color: '#dc2626' }}>{error}</div></Section>}

      {!error && session && tab === 'summary' && (
        <>
          <Section icon="grid" title="Oturum" subtitle="Güne Başla → Günü Bitir aralığı">
            <Grid cols={3}>
              <Stat label="Temsilci" value={session.representativeName || '-'} />
              <Stat label="Başlangıç" value={timeOnly(session.startedAt)} hint={formatDateTime(session.startedAt)} />
              <Stat label="Bitiş" value={session.endedAt ? timeOnly(session.endedAt) : 'Devam ediyor'} hint={session.endedAt ? formatDateTime(session.endedAt) : 'Oturum hâlâ açık'} tone={session.endedAt ? undefined : 'warn'} />
            </Grid>
          </Section>

          <Section icon="chart" title="Gün Metrikleri">
            <Grid cols={3}>
              <Stat label="Oturum Süresi" value={formatMinutes(metrics.sessionMinutes)} />
              <Stat label="Ziyarette Geçen" value={formatMinutes(metrics.visitMinutes)} hint={`Ortalama ${formatMinutes(metrics.avgVisitMinutes)}`} />
              <Stat label="Ziyaret Dışı" value={formatMinutes(metrics.idleMinutes)} hint="Yol + mola + bekleme" tone="warn" />
              <Stat label="Ziyaret" value={metrics.visitCount} hint={`${metrics.customerCount} farklı müşteri`} />
              <Stat label="Tamamlanan" value={metrics.completedCount} tone="good" />
              <Stat label="İptal" value={metrics.cancelledCount} tone={metrics.cancelledCount > 0 ? 'bad' : undefined} />
              <Stat label="Sipariş" value={metrics.orderCount} hint={money(metrics.orderTotal)} />
              <Stat label="Tahsilat" value={metrics.collectionCount} hint={money(metrics.collectionTotal)} />
              <Stat label="Dönüşüm" value={`%${metrics.conversionRate}`} hint="Ziyaretten siparişe" tone={metrics.conversionRate >= 50 ? 'good' : 'warn'} />
            </Grid>
          </Section>

          {mapPoints.length > 0 && (
            <Section icon="map" title="Gün Rotası" subtitle={`${mapPoints.length} konumlu ziyaret`} note="Yalnız GPS yazan ziyaretler haritada görünür.">
              <RouteMap points={mapPoints} numbered showLine height={380} />
            </Section>
          )}
        </>
      )}

      {!error && session && tab === 'visits' && (
        <Section
          icon="clock"
          title="Ziyaretler"
          subtitle={`${visits.length} ziyaret · satıra tıklayın, o ziyaretin zaman çizelgesi açılsın`}
        >
          {visits.length === 0 && <div style={{ color: '#64748b' }}>Bu oturuma bağlı ziyaret yok.</div>}
          {visits.map((v) => {
            const dur = v.endedAt ? Math.round((new Date(v.endedAt) - new Date(v.startedAt)) / 60000) : null;
            const isOpen = expanded === v.id;
            const events = eventsByVisit.get(v.id) || [];
            return (
              <div key={v.id} style={{ borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : v.id)}
                  aria-expanded={isOpen}
                  style={{
                    display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', width: '100%',
                    padding: '10px 12px', background: isOpen ? 'rgba(0,115,234,0.04)' : 'transparent',
                    border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit',
                  }}
                >
                  <span style={{ color: '#94a3b8', width: 12, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>›</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, minWidth: 100 }}>
                    {timeOnly(v.startedAt)} – {v.endedAt ? timeOnly(v.endedAt) : '…'}
                  </span>
                  <span style={{ flex: 1, minWidth: 180 }}>{v.customerTitle || '(Bilinmiyor)'}</span>
                  <span style={{ minWidth: 70, color: '#64748b', fontSize: 12 }}>{dur != null ? formatMinutes(dur) : '-'}</span>
                  {visitStatusBadge(v.status)}
                  {v.reasonCode && (
                    <span style={{ fontSize: 12, color: '#92400e', background: 'rgba(217,119,6,0.10)', padding: '2px 8px', borderRadius: 999 }}>
                      Satamama: {reasonNames[v.reasonCode] || v.reasonCode}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{events.length} olay</span>
                </button>
                {isOpen && (
                  <div style={{ padding: '4px 12px 14px 38px' }}>
                    {v.notes && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{v.notes}</div>}
                    <Timeline events={events} emptyText="Bu ziyarette kayıtlı olay yok." />
                  </div>
                )}
              </div>
            );
          })}

          {/* Hiçbir ziyaretin penceresine düşmeyen olaylar gizlenmemeli. */}
          {orphanEvents.length > 0 && (
            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                Ziyaret dışında kalan {orphanEvents.length} olay
              </div>
              <div style={{ paddingLeft: 26 }}>
                <Timeline events={orphanEvents} emptyText="" />
              </div>
            </div>
          )}
        </Section>
      )}

      {!error && session && tab === 'insights' && (
        <>
          <Section icon="sparkle" title="İçgörüler" subtitle="Oturum metriklerinden kural tabanlı üretilir" note="Bu kartlar bir dil modeli çalıştırmaz; eşikler saha ekibiyle kalibre edilmelidir.">
            <div style={{ display: 'grid', gap: 10 }}>
              {insights.length === 0 && <div style={{ color: '#64748b' }}>Öne çıkan bir bulgu yok.</div>}
              {insights.map((ins, i) => <Insight key={i} tone={ins.tone} title={ins.title} body={ins.body} />)}
            </div>
          </Section>

          {orders.length > 0 && (
            <Section icon="doc" title="Oturum Penceresindeki Siparişler" note="Sipariş ile oturum arasında doğrudan bağ yok; temsilci + zaman aralığına göre eşleştirilir.">
              {orders.map((o) => (
                <div key={o.id} style={{ display: 'flex', gap: 14, padding: '8px 12px', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
                  <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 60 }}>{timeOnly(o.documentDate)}</span>
                  <span style={{ flex: 1 }}>{o.customerTitle || '-'}</span>
                  <strong>{money(o.netAmount)}</strong>
                </div>
              ))}
            </Section>
          )}
        </>
      )}
    </DetailShell>
  );
}

export default FieldVisitDetailPage;
